import {
  AGGREGATED_DERIVED_NOTE,
  ALL_UNSCORED_NOTE,
  missingScoreNote,
  MOVEMENT_BREAK_NOTE,
  previousScoreNote,
} from './copy';

// Which paragraphs sit under the table, and in what order.
//
// Six conditionals rendering the identical sub-tree with only the guard and the string differing.
// As JSX they were six places to edit for one spacing change, and the guards — which are the
// interesting part — were buried inside markup. As data they are a list, the spacing is declared
// once by FeedTableNotes, and a test can ask WHICH note appeared without matching a sentence.
//
// THE GUARDS ARE THE POINT, and they are deliberately asymmetric. Some are computed over the
// RESPONSE and some over the filtered VIEW, and mixing the two puts a false sentence on screen:
//
//   - `unscoredCount` is the VIEW's. The note explains an em dash the reader can see right now, so
//     it has to answer for what is on screen.
//   - `missingCount` is the RESPONSE's. A filter can hide every unscored row while the Movement
//     breaks it explains are still drawn, so this line must not go with them.
//   - `allUnscored` is the RESPONSE's. The missing-score filter makes every visible row unscored by
//     construction, so reading it off the view would claim a cause that is really the filter.
//   - `anyWithoutPrevious` / `anyUnscoredPrevious` are the VIEW's, and both go quiet under
//     aggregation: every aggregated row has a null prevScoreKnowableAt, so the first would fire on
//     the whole table and print "this read holds no earlier occasion for that company", which is
//     false. The aggregated note below says the true thing instead.
//
// Ported here unchanged. If one of these ever needs to move between the two, it is a decision, not
// a tidy-up.
//
// Named `tableNotes.js` rather than `feedTableNotes.js` because this filesystem is case-insensitive
// and that name collides with the component beside it.
export const NOTE_IDS = {
  MISSING_SCORE: 'missing-score',
  MOVEMENT_BREAK: 'movement-break',
  PREVIOUS_SCORE: 'previous-score',
  AGGREGATED_DERIVED: 'aggregated-derived',
  ALL_UNSCORED: 'all-unscored',
  RENAMED: 'renamed',
};

export const tableNotes = ({ result, core, aggregate, hasMemberColumns, summary, viewCounts }) => {
  const { allUnscored, allWithoutPrevious, anyStaleOnUnscored, missingCount, renamedNote } = summary;
  const { unscoredCount, anyWithoutPrevious, anyUnscoredPrevious } = viewCounts;
  const notes = [];

  // The em dash is the point of this page, but it doesn't say so on its own.
  if (unscoredCount > 0) {
    notes.push({
      id: NOTE_IDS.MISSING_SCORE,
      text: missingScoreNote({ hasMemberColumns, isSlim: !!result.slim, anyStale: anyStaleOnUnscored }),
    });
  }

  // The same fact the line above states for the Score column, in the channel the Movement column
  // uses instead of digits.
  if (core.movement && missingCount > 0) {
    notes.push({ id: NOTE_IDS.MOVEMENT_BREAK, text: MOVEMENT_BREAK_NOTE });
  }

  // The blanks in the two derived columns have a different cause from the Score column's, and the
  // one the analyst can act on. Gated on the column being drawn: hidden, it explains nothing that
  // is on screen.
  if ((anyWithoutPrevious || anyUnscoredPrevious) && (core.prevScore || core.scoreDelta)) {
    notes.push({
      id: NOTE_IDS.PREVIOUS_SCORE,
      text: previousScoreNote({
        allWithoutPrevious,
        anyWithoutPrevious,
        anyUnscoredPrevious,
        knowableFloor: result.knowableFloor,
      }),
    });
  }

  // The aggregated view's own answer for the same two columns, and the reason the note above goes
  // quiet: under aggregation their blanks have ONE cause, it belongs to the view rather than to the
  // read, and no widening of the knowable window would fill them.
  if (aggregate && (core.prevScore || core.scoreDelta)) {
    notes.push({ id: NOTE_IDS.AGGREGATED_DERIVED, text: AGGREGATED_DERIVED_NOTE });
  }

  // A whole table of gaps has a cause a table of some gaps doesn't, so it gets its own line rather
  // than a stronger wording of the one above. Gated on the members being visible: under slim the
  // members exist and simply weren't sent, so nothing here can say which of them missed.
  if (allUnscored && hasMemberColumns) {
    notes.push({ id: NOTE_IDS.ALL_UNSCORED, text: ALL_UNSCORED_NOTE });
  }

  if (renamedNote) {
    notes.push({ id: NOTE_IDS.RENAMED, text: renamedNote });
  }

  return notes;
};
